import { toast } from '@/components/toast';
import { deleteCampaign, getCampaigns, GetCampaignsResponse, updateCampaign, UpdateCampaignData } from '@/services/api';
import { logger } from '@/services/logger';
import { ICampaign } from '@d-id/types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';

export const GET_CAMPAIGNS_KEY = 'GET_CAMPAIGNS_KEY';

export function useGetCampaigns(isAnonymous?: boolean) {
    const query = useInfiniteQuery<GetCampaignsResponse>(
        [GET_CAMPAIGNS_KEY],
        ({ pageParam: token }) => {
            if (isAnonymous) {
                return { campaigns: [], token: '' };
            }
            return getCampaigns(token);
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            getNextPageParam: ({ token }) => token,
            onError: err => {
                logger.error('Could not fetch campaigns', { err });
                toast.error({ title: 'Could not fetch campaigns', toastId: `${GET_CAMPAIGNS_KEY}-error` });
            },
        }
    );

    const campaigns = query.data?.pages.flatMap(page => page.campaigns) ?? [];

    return { ...query, data: campaigns };
}

export function useUpdateCampaign(campaign: ICampaign) {
    const queryClient = useQueryClient();

    return useMutation((data: UpdateCampaignData) => updateCampaign(campaign.id, data), {
        onSuccess: async () => {
            await queryClient.invalidateQueries([GET_CAMPAIGNS_KEY]);
        },
        onError: (err: any, data) => {
            logger.error(`Could not update campaign ${campaign.id} with data ${data}`, { err });
            toast.error({ title: 'Failed to update campaign, please try again', toastId: 'update-campaign-error' });
        },
    });
}

export function useDeleteCampaign() {
    const queryClient = useQueryClient();

    return useMutation(deleteCampaign, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([GET_CAMPAIGNS_KEY]);
            toast.success({ title: 'Your campaign was successfully deleted' });
        },
        onError: async (err: any, campaignId) => {
            if (err.response.status !== 404) {
                logger.error(`Could not delete campaign ${campaignId}`, { err });
                toast.error({ title: `Could not delete campaign`, toastId: `delete-campaign-error` });
            }
        },
    });
}

export function useCheckCampaignGif(campaign: ICampaign | null) {
    const isGifUrl = (url: string) =>
        fetch(url, { method: 'HEAD' })
            .then(response => response.ok)
            .catch(() => false);

    return useQuery<boolean>(['check-gif', campaign?.gif_url], () => isGifUrl(campaign?.gif_url ?? ''), {
        enabled: !!campaign?.gif_url,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
}
